<template>
    <div v-if="loaded">
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="model.exportado || model.procedimentosSecundarios.length > 0"
                        :options="form.competencias"
                        :validations="validations.competencia"
                        label="Competência"
                        show-default-option
                        text-field="valor"
                        v-model="model.competenciaId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="model.exportado || !model.competenciaId"
                        :max-date="maxDateDataProcessamento"
                        :min-date="minDateDataProcessamento"
                        :validations="validations.dataProcessamento"
                        label="Data de processamento"
                        v-model="model.dataProcessamento"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="model.exportado"
                        :validations="validations.numeroApac"
                        label="Número da APAC"
                        number
                        v-model="model.numeroApac"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="model.exportado"
                        :validations="validations.numeroApacAnterior"
                        label="Número da APAC anterior"
                        number
                        v-model="model.numeroApacAnterior"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.unidades"
                        :validations="validations.unidadeSolicitante"
                        label="Unidade solicitante"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.unidadeSolicitanteId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.unidades"
                        :validations="validations.unidadeExecutante"
                        label="Unidade executante"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.unidadeExecutanteId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input-autocomplete
                        :disabled="model.exportado || !model.unidadeExecutanteId"
                        :repository="repositories.usuario"
                        :request-query-string-params="{
                            unidade_id: model.unidadeExecutanteId
                        }"
                        :search-fields="fields.autocomplete.usuarioExecutante"
                        :validations="validations.usuarioExecutante"
                        label="Profissional executante"
                        text-field="cidadaoNome"
                        v-model="model.usuarioExecutante"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="model.exportado || !model.competenciaId || !model.procedimentoPrincipal"
                        :max-date="maxDateDataInicialValidade"
                        :min-date="minDateDataInicialValidade"
                        :validations="validations.dataInicialValidade"
                        label="Data inicial da validade"
                        v-model="model.dataInicialValidade"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="model.exportado || !model.dataInicialValidade"
                        :max-date="maxDateDataFinalValidade"
                        :min-date="minDateDataFinalValidade"
                        :validations="validations.dataFinalValidade"
                        label="Data final da validade"
                        right
                        v-model="model.dataFinalValidade"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-area
                title="Paciente"
                type="fieldset"
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="model.exportado || model.procedimentosSecundarios.length > 0"
                            :repository="repositories.paciente"
                            :request-query-string-params="{
                                with: ['cidadao.paisNascimento', 'cadastroIndividual']
                            }"
                            :search-fields="fields.autocomplete.paciente"
                            :text-field="p => p.cidadao.nome"
                            :validations="validations.paciente"
                            label="Paciente"
                            v-model="model.paciente"
                        >
                            <template #itemLabel="{ item }">
                                {{ item.cidadao.nome }}
                                <template v-if="item.cidadao.cns">
                                    - {{ formatCns(item.cidadao.cns) }}
                                </template>
                            </template>
                        </rkm-form-input-autocomplete>
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.dataNascimento
                                ? others.moment(model.paciente.cidadao.dataNascimento).format('DD/MM/YYYY')
                                : '')"
                            disabled
                            label="Data de nascimento"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.prontuario)"
                            disabled
                            label="Prontuário"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.cns ? formatCns(model.paciente.cidadao.cns) : '')"
                            disabled
                            label="CNS"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.cpf ? formatCpf(model.paciente.cidadao.cpf) : '')"
                            disabled
                            label="CPF"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.nomeMae)"
                            disabled
                            label="Nome da mãe"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.sexo.descricao)"
                            disabled
                            label="Sexo"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.racaCor.descricao)"
                            disabled
                            label="Raça/cor"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.etniaIndigena.descricao)"
                            disabled
                            label="Etnia indígena"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="9">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.domicilio.endereco.completo)"
                            disabled
                            label="Endereço"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.domicilio.endereco.logradouro.cep
                                ? formatCep(model.paciente.cidadao.domicilio.endereco.logradouro.cep)
                                : '')"
                            disabled
                            label="CEP"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.paisNascimento.nome)"
                            disabled
                            label="País de nacionalidade"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.email)"
                            disabled
                            label="E-mail"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cidadao.telefonePrincipal
                                ? formatPhoneOrCellPhone(model.paciente.cidadao.telefonePrincipal.numero)
                                : '')"
                            disabled
                            label="Telefone"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="getSafely(() => model.paciente.cadastroIndividual.situacaoRuaId ? 'SIM' : 'NÃO')"
                            disabled
                            label="Em situação de rua?"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                title="Procedimentos realizados"
                type="fieldset"
            >
                <rkm-area title="Procedimento principal">
                    <rkm-row>
                        <rkm-col cols="5">
                            <rkm-form-input-autocomplete
                                :disabled="model.exportado || model.procedimentosSecundarios.length > 0"
                                :repository="repositories.ocupacao"
                                :search-fields="fields.autocomplete.ocupacao"
                                :text-field="o => `${o.codigo} - ${o.nome}`"
                                :validations="validations.procedimentoPrincipalOcupacao"
                                label="CBO"
                                v-model="model.procedimentoPrincipalOcupacao"
                            />
                        </rkm-col>

                        <rkm-col cols="5">
                            <rkm-form-input-autocomplete
                                :disabled="model.exportado
                                    || !model.competenciaId
                                    || !model.paciente
                                    || !model.procedimentoPrincipalOcupacao
                                    || model.procedimentosSecundarios.length > 0"
                                :repository="repositories.procedimento"
                                :request-query-string-params="{
                                    instrumento_registro: enums.registro.APAC_PROCEDIMENTO_PRINCIPAL,
                                    ocupacao_id: getSafely(() => model.procedimentoPrincipalOcupacao.id),
                                    competencia_id: model.competenciaId,
                                    paciente_id: getSafely(() => model.paciente.id),
                                    with_atributos_complementares: true,
                                    only_oci: true
                                }"
                                :search-fields="fields.autocomplete.procedimentoPrincipal"
                                :text-field="p => `${formatProcedimento(p.codigo)} - ${p.nome}`"
                                :validations="validations.procedimentoPrincipal"
                                label="Procedimento"
                                v-model="model.procedimentoPrincipal"
                            />
                        </rkm-col>

                        <rkm-col cols="2">
                            <rkm-form-input
                                :disabled="model.exportado || !model.procedimentoPrincipal"
                                :validations="validations.procedimentoPrincipalQuantidade"
                                label="Quantidade"
                                number
                                v-model="model.procedimentoPrincipalQuantidade"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-input-autocomplete
                                :disabled="model.exportado || !model.procedimentoPrincipal"
                                :repository="repositories.cid"
                                :request-query-string-params="{
                                    procedimento_id: getSafely(() => model.procedimentoPrincipal.id),
                                    principal: true,
                                }"
                                :search-fields="fields.autocomplete.cidPrincipal"
                                :text-field="p => `${formatCid(p.codigo)} - ${p.nome}`"
                                :validations="validations.procedimentoPrincipalCidPrincipal"
                                label="CID principal"
                                v-model="model.procedimentoPrincipalCidPrincipal"
                            />
                        </rkm-col>

                        <rkm-col>
                            <rkm-form-input-autocomplete
                                :disabled="model.exportado || !model.procedimentoPrincipal"
                                :repository="repositories.cid"
                                :request-query-string-params="{
                                    procedimento_id: getSafely(() => model.procedimentoPrincipal.id),
                                    principal: false,
                                }"
                                :search-fields="fields.autocomplete.cidSecundario"
                                :text-field="p => `${formatCid(p.codigo)} - ${p.nome}`"
                                :validations="validations.procedimentoPrincipalCidSecundario"
                                label="CID secundário"
                                v-model="model.procedimentoPrincipalCidSecundario"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="6">
                            <rkm-form-input-autocomplete
                                :disabled="model.exportado || !model.procedimentoPrincipal"
                                :repository="repositories.servico"
                                :request-query-string-params="{
                                    procedimento_id: getSafely(() => model.procedimentoPrincipal.id)
                                }"
                                :search-fields="fields.autocomplete.servico"
                                :validations="validations.procedimentoPrincipalServico"
                                label="Serviço"
                                text-field="nome"
                                v-model="model.procedimentoPrincipalServico"
                            />
                        </rkm-col>

                        <rkm-col cols="6">
                            <rkm-form-input-autocomplete
                                :disabled="model.exportado || !model.procedimentoPrincipalServico"
                                :repository="repositories.classificacao"
                                :request-query-string-params="{
                                    procedimento_id: getSafely(() => model.procedimentoPrincipal.id),
                                    servico_id: getSafely(() => model.procedimentoPrincipalServico.id),
                                }"
                                :search-fields="fields.autocomplete.classificacao"
                                :validations="validations.procedimentoPrincipalClassificacao"
                                label="Classificação"
                                text-field="nome"
                                v-model="model.procedimentoPrincipalClassificacao"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="4">
                            <rkm-form-input
                                :disabled="model.exportado"
                                :validations="validations.procedimentoPrincipalCnpjCessaoCredito"
                                label="CNPJ cessão de crédito"
                                v-mask="mask.cnpj"
                                v-model="model.procedimentoPrincipalCnpjCessaoCredito"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-input
                                :disabled="model.exportado"
                                :validations="validations.procedimentoPrincipalNumeroNotaFiscalCessao"
                                label="Número da nota fiscal de cessão"
                                number
                                v-model="model.procedimentoPrincipalNumeroNotaFiscalCessao"
                            />
                        </rkm-col>

                        <rkm-col cols="4">
                            <rkm-form-input
                                :disabled="model.exportado"
                                :validations="validations.procedimentoPrincipalUnidadePrestadoraTerceiroCnes"
                                label="CNES da unidade prestadora de terceiro"
                                number
                                v-model="model.procedimentoPrincipalUnidadePrestadoraTerceiroCnes"
                            />
                        </rkm-col>
                    </rkm-row>
                </rkm-area>

                <ProcedimentoSecundario
                    :data="{ additionals, form, model }"
                    ref="ProcedimentoSecundario"
                />
            </rkm-area>

            <rkm-area
                title="Dados complementares"
                type="fieldset"
            >
                <template v-if="model.procedimentoPrincipal">
                    <template v-if="!procedimentoExigeDadosComplementares">
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-input-autocomplete
                                    :disabled="model.exportado"
                                    :repository="repositories.cid"
                                    :request-query-string-params="{
                                        procedimento_id: getSafely(() => model.procedimentoPrincipal.id),
                                        principal: true,
                                    }"
                                    :search-fields="fields.autocomplete.cidPrincipal"
                                    :text-field="p => `${formatCid(p.codigo)} - ${p.nome}`"
                                    :validations="validations.laudoGeral.cidPrincipal"
                                    label="CID principal"
                                    v-model="model.laudoGeral.cidPrincipal"
                                />
                            </rkm-col>

                            <rkm-col>
                                <rkm-form-input-autocomplete
                                    :disabled="model.exportado"
                                    :repository="repositories.cid"
                                    :request-query-string-params="{
                                        procedimento_id: getSafely(() => model.procedimentoPrincipal.id),
                                        principal: false,
                                    }"
                                    :search-fields="fields.autocomplete.cidSecundario"
                                    :text-field="p => `${formatCid(p.codigo)} - ${p.nome}`"
                                    :validations="validations.laudoGeral.cidSecundario"
                                    label="CID secundário"
                                    v-model="model.laudoGeral.cidSecundario"
                                />
                            </rkm-col>

                            <rkm-col cols="3">
                                <rkm-form-date-picker
                                    :disabled="model.exportado"
                                    :max-date="additionals.dataAtual"
                                    :validations="validations.laudoGeral.dataIdentificacaoPatologica"
                                    label="Data da identificação patológica"
                                    right
                                    v-model="model.laudoGeral.dataIdentificacaoPatologica"
                                />
                            </rkm-col>
                        </rkm-row>
                    </template>
                </template>

                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-form-input-autocomplete
                            :disabled="model.exportado || !model.procedimentoPrincipal"
                            :repository="repositories.cid"
                            :search-fields="fields.autocomplete.cidCausaAssociada"
                            :request-query-string-params="{
                                procedimento_id: getSafely(() => model.procedimentoPrincipal.id),
                            }"
                            :text-field="p => `${formatCid(p.codigo)} - ${p.nome}`"
                            :validations="validations.cidCausaAssociada"
                            label="CID causas associadas"
                            v-model="model.cidCausaAssociada"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-row>
                <rkm-col cols="8">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.motivosSaidaPermanencia"
                        :validations="validations.motivoSaidaPermanencia"
                        label="Motivo de saída/permanência"
                        show-default-option
                        text-field="nome"
                        v-model="model.motivoSaidaPermanenciaId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-date-picker
                        :disabled="model.exportado || !model.competenciaId"
                        :max-date="maxDateDataOcorrencia"
                        :min-date="minDateDataOcorrencia"
                        :validations="validations.dataOcorrencia"
                        label="Data da ocorrência (alta, transferência ou óbito)"
                        right
                        v-model="model.dataOcorrencia"
                    />
                </rkm-col>
            </rkm-row>

             <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.tiposAtendimento"
                        :validations="validations.tipoAtendimento"
                        label="Tipo de atendimento"
                        show-default-option
                        text-field="nome"
                        v-model="model.tipoAtendimentoId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.carateresAtendimento"
                        :validations="validations.caraterAtendimento"
                        label="Caráter do atendimento"
                        show-default-option
                        text-field="nome"
                        v-model="model.caraterAtendimentoId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.tiposApac"
                        :validations="validations.tipoApac"
                        label="Tipo de APAC"
                        show-default-option
                        text-field="nome"
                        v-model="model.tipoApacId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="9">
                    <rkm-form-input-autocomplete
                        :disabled="model.exportado"
                        :repository="repositories.usuario"
                        :search-fields="fields.autocomplete.usuarioResponsavel"
                        :validations="validations.usuarioResponsavel"
                        label="Profissional responsável"
                        text-field="cidadaoNome"
                        v-model="model.usuarioResponsavel"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="model.exportado || !model.dataInicialValidade"
                        :max-date="maxDateDataSolicitacao"
                        :validations="validations.dataSolicitacao"
                        label="Data da solicitação"
                        right
                        v-model="model.dataSolicitacao"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="possuiAutorizador">
                <rkm-col cols="4">
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.autorizadores"
                        :validations="validations.usuarioAutorizadorCns"
                        label="Selecionar autorizador"
                        show-default-option
                        text-field="nome"
                        v-model="model.usuarioAutorizadorCns"
                        value-field="cns"
                    />

                    <small
                        class="text-danger d-block mt-n5"
                        v-if="!model.exportado && !possuiAutorizadorAtivo"
                    >
                        Não existem autorizadores ativos cadastrados.
                    </small>
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input
                        :value="model.usuarioAutorizadorNome"
                        disabled
                        label="Profissional autorizador"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input
                        :value="usuarioAutorizadorCnsFormatado"
                        disabled
                        label="CNS do profissional autorizador"
                    />
                </rkm-col>
            </rkm-row>

            <small
                class="text-danger d-block mb-3"
                v-if="!possuiAutorizador && !model.exportado"
            >
                Não existem autorizadores ativos cadastrados.
            </small>

            <rkm-row>

                  <rkm-col cols="3">
                    <rkm-form-date-picker
                        :disabled="model.exportado"
                        :max-date="additionals.dataAtual"
                        :validations="validations.dataAutorizacao"
                        label="Data da autorização"
                        right
                        v-model="model.dataAutorizacao"
                    />
                </rkm-col>


                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="model.exportado"
                        :validations="validations.codigoEmissor"
                        label="Código do emissor"
                        uppercase
                        v-model="model.codigoEmissor"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-select
                        :disabled="model.exportado"
                        :options="form.fontesOrcamentarias"
                        :validations="validations.fonteOrcamentaria"
                        label="Fonte orçamentária"
                        show-default-option
                        text-field="nome"
                        v-model="model.fonteOrcamentariaId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-form-checkbox
                        :disabled="model.exportado"
                        :label="false"
                        v-model="model.emendaParlamentar"
                    >
                        Emenda parlamentar
                    </rkm-form-checkbox>
                </rkm-col>
            </rkm-row>
        </rkm-form>
    </div>
</template>

<script>
// Components.
import ProcedimentoSecundario from './components/procedimento-secundario/Index'

// Mixins.
import { repository, error, formats, object, data, stringCase, mask, unmask } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

// Moment.
import moment from 'moment'

// Enums.
import registro from '@/enums/sigtap/registro'
import detalhe from '@/enums/sigtap/detalhe'

export default {
    components: { ProcedimentoSecundario },
    computed: {
        fields() {

            return {
                autocomplete: {
                    usuarioExecutante: [
                        { key: 'cadastroCidadao.nome', searchable: true },
                    ],
                    paciente: [
                        { key: 'cidadao.nome', searchable: true },
                    ],
                    procedimentoPrincipal: [
                        { key: 'procedimento.codigo', searchable: true },
                        { key: 'procedimento.nome', searchable: true },
                    ],
                    ocupacao: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    cidPrincipal: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    cidSecundario: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    cidCausaAssociada: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    servico: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    classificacao: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    usuarioResponsavel: [
                        { key: 'cadastroCidadao.nome', searchable: true },
                    ],
                }
            }
        },
        validations() {

            return {
                competencia: { required: true },
                dataProcessamento: { required: true },
                numeroApac: { required: true, min: 13, max: 13 },
                numeroApacAnterior: { min: 13, max: 13 },
                unidadeSolicitante: {},
                unidadeExecutante: { required: true },
                usuarioExecutante: { required: true },
                dataInicialValidade: { required: true },
                dataFinalValidade: { required: true },
                paciente: { required: true },
                procedimentoPrincipalOcupacao: { required: true },
                procedimentoPrincipal: { required: true },
                procedimentoPrincipalQuantidade: {
                    required: true,
                    min_value: 1,
                    max_value: this.model.procedimentoPrincipal ? this.model.procedimentoPrincipal.quantidadeMaximaExecucao : null,
                },
                procedimentoPrincipalCidPrincipal: { required: this.model.procedimentoPrincipal && !!this.model.procedimentoPrincipal.hasCidPrincipal },
                procedimentoPrincipalCidSecundario: {},
                procedimentoPrincipalServico: {},
                procedimentoPrincipalClassificacao: {},
                procedimentoPrincipalCnpjCessaoCredito: {
                    min_max_without_mask: 14,
                    cnpj: true,
                },
                procedimentoPrincipalNumeroNotaFiscalCessao: { max: 6 },
                procedimentoPrincipalUnidadePrestadoraTerceiroCnes: { min: 7, max: 7 },
                laudoGeral: {
                    cidPrincipal: { required: true },
                    cidSecundario: {},
                    dataIdentificacaoPatologica: { required: this.laudoGeralDataIdentificacaoPatologicaRequired  },
                },
                cidCausaAssociada: {},
                motivoSaidaPermanencia: { required: true },
                dataOcorrencia: {},
                tipoAtendimento: {},
                caraterAtendimento: { required: true },
                tipoApac: { required: true },
                usuarioResponsavel: { required: true },
                dataSolicitacao: { required: true },
                usuarioAutorizadorCns: { required: true },
                dataAutorizacao: { required: true },
                codigoEmissor: { required: true, min: 10, max: 10 },
                fonteOrcamentaria: {},
            }
        },
        possuiAutorizador() {

            return this.form.autorizadores.length > 0
        },
        possuiAutorizadorAtivo() {

            return this.form.autorizadores.some(autorizador => autorizador.ativo)
        },
        usuarioAutorizadorCnsFormatado() {

            return this.model.usuarioAutorizadorCns
                ? this.formatCns(this.model.usuarioAutorizadorCns) : ''
        },
        procedimentoComValidadeDuasCompetencias() {

            if (!this.model.procedimentoPrincipal) {

                return false
            }

            return this.model.procedimentoPrincipal.atributosComplementares
                .includes(this.enums.detalhe.APAC_COM_VALIDADE_FIXA_DE_2_COMPETENCIAS)
        },
        procedimentoExigeDadosComplementares() {

            if (!this.model.procedimentoPrincipal) {

                return false
            }

            return this.model.procedimentoPrincipal.atributosComplementares
                .includes(this.enums.detalhe.EXIGE_REGISTRO_NA_APAC_DE_DADOS_COMPLEMENTARES)
        },
        laudoGeralDataIdentificacaoPatologicaRequired() {

            if (!this.model.procedimentoPrincipal) {

                return false
            }

            return this.model.procedimentoPrincipal.codigo.substr(0, 4) === '0901'
                && this.model.procedimentosSecundarios.some(pc => pc.procedimento.atributosComplementares
                    .includes(this.enums.detalhe.EXIGE_DATA_DO_RESULTADO_DIAGNOSTICO_DE_NEOPLASIA))
        },
        minDateDataProcessamento() {

            if (!this.competencia) {

                return null
            }

            return this.others.moment(this.competencia.inicio).format('DD/MM/YYYY')
        },
        maxDateDataProcessamento() {

            if (!this.competencia) {

                return null
            }

            const dataAtual = this.others.moment(this.additionals.dataAtual, 'DD/MM/YYYY')
            const dataFinal = this.others.moment(this.competencia.fim).add(1, 'M').endOf('month')

            if (dataFinal > dataAtual) {

                return dataAtual.format('DD/MM/YYYY')
            }

            return dataFinal.format('DD/MM/YYYY')
        },
        minDateDataInicialValidade() {

            if (!this.competencia) {

                return null
            }

            return this.others.moment(this.competencia.inicio)
                .subtract(this.procedimentoComValidadeDuasCompetencias ? 1 : 2, 'M')
                .startOf('month')
                .format('DD/MM/YYYY')
        },
        maxDateDataInicialValidade() {

            if (!this.competencia) {

                return null
            }

            const dataAtual = this.others.moment(this.additionals.dataAtual, 'DD/MM/YYYY')
            const dataFinalCompetencia = this.others.moment(this.competencia.fim)

            if (dataFinalCompetencia > dataAtual) {

                return dataAtual.format('DD/MM/YYYY')
            }

            return dataFinalCompetencia.format('DD/MM/YYYY')
        },
        minDateDataFinalValidade() {

            if (!this.model.dataInicialValidade) {

                return null
            }

            return this.others.moment(this.model.dataInicialValidade).format('DD/MM/YYYY')
        },
        maxDateDataFinalValidade() {

            if (!this.model.dataInicialValidade) {

                return null
            }

            return this.others.moment(this.model.dataInicialValidade)
                .add(this.procedimentoComValidadeDuasCompetencias ? 1 : 2, 'M')
                .endOf('month')
                .format('DD/MM/YYYY')
        },
        maxDateDataSolicitacao() {

            if (!this.model.dataInicialValidade) {

                return null
            }

            return this.others.moment(this.model.dataInicialValidade).format('DD/MM/YYYY')
        },
        minDateDataOcorrencia() {

            if (!this.competencia) {

                return null
            }

            return this.others.moment(this.competencia.inicio).format('DD/MM/YYYY')
        },
        maxDateDataOcorrencia() {

            if (!this.competencia) {

                return null
            }

            return this.others.moment(this.competencia.fim).format('DD/MM/YYYY')
        },
        competencia() {

            if (!this.model.competenciaId) {

                return null
            }

            const competencia = this.form.competencias.find(c => c.id === this.model.competenciaId).valor
            const ano = parseInt(competencia.substring(0, 4))
            const mes = parseInt(competencia.substring(4, 6))

            return {
                inicio: this.others.moment([ano, mes - 1]).startOf('month').toDate(),
                fim: this.others.moment([ano, mes - 1]).endOf('month').toDate()
            }
        }
    },
    created() {

        this.repositories = {
            procedimentoApac: this.getConectaSusRepository('apac.procedimento'),
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            cid: this.getConectaSusRepository('sigtap.cid'),
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao'),
            servico: this.getConectaSusRepository('sigtap.servico'),
            classificacao: this.getConectaSusRepository('sigtap.classificacao'),
        }

        this.load()
    },
    data: () => ({
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        enums: { registro, detalhe },
        others: { moment },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            const repository = this.id
                ? this.repositories.procedimentoApac.edit(this.id)
                : this.repositories.procedimentoApac.create({ origem: 'cadastro' })

            repository
                .then(({ data }) => {

                    this.formatModelDates(data.model)

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.addWatchers()

                    this.$emit('model-load', this.model)

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        formatModelDates(model) {

            if (model.dataProcessamento) {

                model.dataProcessamento = this.others.moment(model.dataProcessamento, 'DD/MM/YYYY').toDate()
            }

            if (model.dataInicialValidade) {

                model.dataInicialValidade = this.others.moment(model.dataInicialValidade, 'DD/MM/YYYY').toDate()
            }

            if (model.dataFinalValidade) {

                model.dataFinalValidade = this.others.moment(model.dataFinalValidade, 'DD/MM/YYYY').toDate()
            }

            if (model.dataOcorrencia) {

                model.dataOcorrencia = this.others.moment(model.dataOcorrencia, 'DD/MM/YYYY').toDate()
            }

            if (model.dataSolicitacao) {

                model.dataSolicitacao = this.others.moment(model.dataSolicitacao, 'DD/MM/YYYY').toDate()
            }

            if (model.dataAutorizacao) {

                model.dataAutorizacao = this.others.moment(model.dataAutorizacao, 'DD/MM/YYYY').toDate()
            }

            if (model.laudoGeral.dataIdentificacaoPatologica) {

                model.laudoGeral.dataIdentificacaoPatologica = this.others.moment(model.laudoGeral.dataIdentificacaoPatologica, 'DD/MM/YYYY').toDate()
            }
        },
        addWatchers() {

            this.$watch('model.competenciaId', () => {

                this.model.dataProcessamento = null
                this.model.dataInicialValidade = null
                this.model.dataFinalValidade = null
                this.model.procedimentoPrincipal = null
                this.model.dataOcorrencia = null
            })

            this.$watch('model.unidadeExecutanteId', () => {

                this.model.usuarioExecutante = null
            })

            this.$watch('model.dataInicialValidade', () => {

                this.model.dataFinalValidade = null
                this.model.dataSolicitacao = null
            })

            this.$watch('model.paciente', () => {

                this.model.procedimentoPrincipal = null
            })

            this.$watch('model.procedimentoPrincipalOcupacao', () => {

                this.model.procedimentoPrincipal = null
            })

            this.$watch('model.usuarioAutorizadorCns', cns => {

                const autorizador = this.form.autorizadores.find(a => a.cns === cns)

                this.model.usuarioAutorizadorNome = autorizador ? autorizador.nome.substring(0, 30) : null
            })

            this.$watch('model.procedimentoPrincipal', () => {

                this.model.dataInicialValidade = null
                this.model.dataFinalValidade = null

                this.model.procedimentoPrincipalQuantidade = null
                this.model.procedimentoPrincipalCidPrincipal = null
                this.model.procedimentoPrincipalCidSecundario = null
                this.model.procedimentoPrincipalServico = null
                this.model.procedimentoPrincipalClassificacao = null

                this.model.laudoGeral.cidPrincipal = null
                this.model.laudoGeral.cidSecundario = null
                this.model.laudoGeral.dataIdentificacaoPatologica = null
                this.model.cidCausaAssociada = null
            })
        },
        save() {

            Promise.all([
                this.$refs.Form.validate(),
                this.$refs.ProcedimentoSecundario.validate(),
            ])
                .then(([formValid, procedimentosValid]) => {

                    if (!formValid || !procedimentosValid) return

                    this.setLoading()

                    const repository = this.id
                        ? this.repositories.procedimentoApac.update(this.id, this.prepareData())
                        : this.repositories.procedimentoApac.store(this.prepareData())

                    repository
                        .then(() => {

                            this.showSaveNotification()

                            this.$emit('save')
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        prepareData() {

            const data = this.clone(this.model)

            data.origem = 'cadastro'

            delete data.exportado

            this.createIdKeysToObject(data, [
                'usuarioExecutante',
                'paciente',
                'procedimentoPrincipalOcupacao',
                'procedimentoPrincipal',
                'procedimentoPrincipalCidPrincipal',
                'procedimentoPrincipalCidSecundario',
                'procedimentoPrincipalServico',
                'procedimentoPrincipalClassificacao',
                'cidCausaAssociada',
                'usuarioResponsavel',
            ])

            this.createIdKeysToObject(data.laudoGeral, [
                'cidPrincipal',
                'cidSecundario',
            ])

            this.formateDates(data, [
                'dataProcessamento',
                'dataInicialValidade',
                'dataFinalValidade',
                'dataOcorrencia',
                'dataSolicitacao',
                'dataAutorizacao',
            ], 'DD/MM/YYYY')

            this.formateDates(data.laudoGeral, [
                'dataIdentificacaoPatologica',
            ], 'DD/MM/YYYY')

            if (data.procedimentoPrincipalCnpjCessaoCredito) {

                data.procedimentoPrincipalCnpjCessaoCredito = this.unMaskCnpj(data.procedimentoPrincipalCnpjCessaoCredito)
            }

            data.procedimentosSecundarios.map(procedimento => {

                if (!procedimento.saved) {

                    procedimento.id = null
                }

                if (procedimento.cnpjCessaoCredito) {

                    procedimento.cnpjCessaoCredito = this.unMaskCnpj(procedimento.cnpjCessaoCredito)
                }

                this.createIdKeysToObject(procedimento, [
                    'ocupacao',
                    'procedimento',
                    'cidPrincipal',
                    'cidSecundario',
                    'servico',
                    'classificacao',
                ])

                delete procedimento.saved
            })

            return this.camelObjectKeysToSnakeRecursive(data)
        }
    },
    mixins: [repository, error, formats, object, data, stringCase, mask, unmask],
    props: {
        id: { type: Number, default: null }
    },
}
</script>